import { HttpClient, HttpHeaders, HttpParams } from '@angular/common/http';
import { inject, Injectable } from '@angular/core';
import { AuthUtils } from 'app/core/auth/auth.utils';
import { UserService } from 'app/core/user/user.service';
import { environment } from 'environments/environment';
import { catchError, Observable, of, switchMap, tap, throwError } from 'rxjs';

@Injectable({ providedIn: 'root' })
export class AuthService {
    private _authenticated: boolean = false;
    private _httpClient = inject(HttpClient);
    private _userService = inject(UserService);
    private _baseApiUrl = environment.oauthBaseTokenEndpoint;

    // -----------------------------------------------------------------------------------------------------
    // @ Accessors
    // -----------------------------------------------------------------------------------------------------

    /**
     * Setter & getter for access token
     */
    set accessToken(token: string) {
        localStorage.setItem('accessToken', token);
    }

    get accessToken(): string {
        return localStorage.getItem('accessToken') ?? '';
    }

    set refreshToken(token: string) {
        localStorage.setItem('refreshToken', token);
    }

    get refreshToken(): string {
        return localStorage.getItem('refreshToken') ?? '';
    }

    // -----------------------------------------------------------------------------------------------------
    // @ Public methods
    // -----------------------------------------------------------------------------------------------------

    /**
     * Forgot password
     *
     * @param email
     */
    forgotPassword(email: string): Observable<any> {
        return this._httpClient.post('api/auth/forgot-password', email);
    }   

    /**
     * Sign in using the access token
     */
    signInUsingToken(): Observable<any> {
        // Decode the token and assign the user object to the user service
        const user = AuthUtils._decodeTokenToUser(this.accessToken);
        if (user) {
            this._userService.user = {
                id: user.id,
                name: user.givenName,
                email: user.email,
                fullName: user.name || '', // Opcional, si no existe se asigna una cadena vacía
                firstName: user.givenName || '', // Opcional, si no existe se asigna una cadena vacía
                lastName: user.familyName || '', // Opcional, si no existe se asigna una cadena vacía
                avatar: user.avatar || '', // Opcional, si no existe se asigna una cadena vacía
                status: user.status || 'active', // Opcional, se asigna 'active' por defecto
            };
        }
        return of(true);
    }

    /**
     * Sign out
     */
    signOut(): Observable<any> {
        // Remove the access token from the local storage
        localStorage.removeItem('accessToken');
        localStorage.removeItem('refreshToken');

        // Set the authenticated flag to false
        this._authenticated = false;

        // Return the observable
        return of(true);
    }

    /**
     * Sign up
     *
     * @param user
     */
    signUp(user: {
        name: string;
        email: string;
        password: string;
        company: string;
    }): Observable<any> {
        return this._httpClient.post('api/auth/sign-up', user);
    }

    /**
     * Unlock session
     *
     * @param credentials
     */
    unlockSession(credentials: {
        email: string;
        password: string;
    }): Observable<any> {
        return this._httpClient.post('api/auth/unlock-session', credentials);
    }

    callRefreshToken(): Observable<any> {
        // Check if the refresh token is available
        if (!this.refreshToken) {
            return of(false);
        }

        // Prepare the request body
        const body = {
            grant_type: 'refresh_token',
            refresh_token: this.refreshToken,
            client_id: environment.oauthClientId
        };

        // Prepare the request headers
        const headers = new HttpHeaders({
            'Content-Type': 'application/json',
        });

        // Make the request to refresh the token
        return this._httpClient.post<any>(`${this._baseApiUrl}/authentication/oauth/refresh`, body, { headers });
    }

    /**
     * Check the authentication status
     */
    check(): Observable<boolean> {
        // Check if the user is logged in
        if (this._authenticated) {
            return of(true);
        }

        // Check the access token availability
        if (!this.accessToken) {
            return of(false);
        }

        // Check the access token expire date
        if (AuthUtils.isTokenExpired(this.accessToken)) {
            //Check for refresh token
            if (this.refreshToken && !AuthUtils.isTokenExpired(this.refreshToken)) {
                return this.callRefreshToken().pipe(
                    tap((tokens) => {
                      // Store the access token in the local storage
                        this.accessToken = tokens.access_token;
                        this.refreshToken = tokens.refresh_token;

                        // Set the authenticated flag to true
                        this._authenticated = true;      
                        
                        // Decode the token and assign the user object to the user service
                        const user = AuthUtils._decodeTokenToUser(tokens.access_token);
                        if (user) {
                            this._userService.user = {
                                id: user.id,
                                name: user.givenName,
                                email: user.email,
                                fullName: user.name || '', // Opcional, si no existe se asigna una cadena vacía
                                firstName: user.givenName || '', // Opcional, si no existe se asigna una cadena vacía
                                lastName: user.familyName || '', // Opcional, si no existe se asigna una cadena vacía
                                avatar: user.avatar || '', // Opcional, si no existe se asigna una cadena vacía
                                status: user.status || 'active', // Opcional, se asigna 'active' por defecto
                            };
                        }
                        return of(true);
                    }),
                    switchMap(() => of(true)), // Indicate successful authentication after refresh
                    catchError((error) => {
                      console.error('Error refreshing token:', error);

                      return of(false);
                    })
                  );
            }else{
                return of(false);
            }
        }
        // If the access token exists, and it didn't expire, sign in using it
        return this.signInUsingToken();
    }

    exchangeCodeForTokens(code: string,
        codeVerifier: string,
        redirectUri: string): Observable<any>{
            const body = new HttpParams()
            .set('grant_type', 'authorization_code')
            .set('code', code)
            .set('redirect_uri', redirectUri)
            .set('client_id', environment.oauthClientId)
            .set('code_verifier', codeVerifier);

            const headers = new HttpHeaders({
            'Content-Type': 'application/x-www-form-urlencoded',
            });

            return this._httpClient.post<any>(`${this._baseApiUrl}/authentication/oauth/token`, body.toString(), { headers }).pipe(
                switchMap((response: any) => {
                    // Store the access token in the local storage
                    this.accessToken = response.access_token;
                    this.refreshToken = response.refresh_token;

                    // Set the authenticated flag to true
                    this._authenticated = true;

                    // Decode the token and assign the user object to the user service
                    const user = AuthUtils._decodeTokenToUser(response.access_token);
                    if (user) {
                        this._userService.user = {
                            id: user.id,
                            name: user.givenName,
                            email: user.email,
                            fullName: user.name || '', // Opcional, si no existe se asigna una cadena vacía
                            firstName: user.givenName || '', // Opcional, si no existe se asigna una cadena vacía
                            lastName: user.familyName || '', // Opcional, si no existe se asigna una cadena vacía
                            avatar: user.avatar || '', // Opcional, si no existe se asigna una cadena vacía
                            status: user.status || 'active', // Opcional, se asigna 'active' por defecto
                        };
                    }

                    // Return a new observable with the response
                    return of(response);
                }),
                catchError((error) => {

                    console.error('Error exchanging code for tokens', error);
                    return of(null); // or throwError(error) based on your error handling strategy
                })
            );
    }
}
